/**
 * The app's one transient voice, rebuilt around the thing it was worst at:
 * being read.
 *
 * The previous version gave every toast a flat 5 s (6.5 s in her voice) and no
 * way to keep it. Her toast budget is 120 characters of Lithuanian, which at
 * 6.5 s is about eighteen characters a second — at or past the speed a person
 * reads a sentence they have never seen, on a phone, one-handed, usually while
 * doing something else. A line you cannot finish is a line that was not
 * written. So duration now comes from the length of what she actually said,
 * with a floor for the short ones and a ceiling so nothing camps.
 *
 * Three further things it could not do, each of which cost something real:
 *
 * - **Pause.** The timer handle was never kept, so a toast could not be
 *   stopped, held or resumed. Now a finger on the toast pauses it and a tap
 *   holds it open until tapped again.
 * - **Wait its turn.** A fourth toast silently deleted the oldest, mid-life,
 *   including one that might have been holding the only undo for a deletion
 *   that had already committed. Overflow now queues; undo and trophies can
 *   never be dropped from it at all.
 * - **Stay out of the way.** Everything raised while she was performing landed
 *   underneath the performance. There is now a gate: the stage closes it on
 *   mount, and anything that is not an error or an undo offer waits behind it.
 *
 * What did not change: `showToast`'s signature. Five delete call sites hand
 * their only route back to a committed deletion to this function, and this was
 * not the release to make them prove themselves again.
 */

import { create } from 'zustand'
import type { CueName } from '../sound/manifest.js'

export interface ToastAction {
  label: string
  run: () => void | Promise<void>
}

/**
 * What a toast is, which decides how it looks, how long it stays and whether
 * it is allowed past a closed gate.
 *
 * - `utility` — the app talking. Lime, quiet, the exact look it always had.
 * - `persona` — her talking. Her colour, her mark, bigger type.
 * - `error` — something failed. Danger tint, and announced assertively.
 * - `trophy` — a once-ever moment. Her arrival animation and a sound.
 */
export type ToastKind = 'utility' | 'persona' | 'error' | 'trophy'

export interface ToastItem {
  id: number
  message: string
  kind: ToastKind
  action?: ToastAction
  /**
   * Reply chips — the talk-back affordance on persona toasts. Never attached
   * alongside an `action`: an undo offer is a safety window, not a venue.
   * Chips make nothing urgent — gate bypass, queue shedding and the solo
   * rule all keep keying on `action` alone.
   */
  chips?: ReadonlyArray<ToastAction>
  /** Rendered only into the live region, for screen readers. */
  srOnly?: boolean
  /** Played when this toast becomes visible, not when it is enqueued. */
  cue?: CueName
  durationMs: number
  /** Held open by a tap; stays until tapped again. */
  held?: boolean
  /** Animating out. Kept mounted for one exit animation, then dropped. */
  leaving?: boolean
}

/**
 * Reading speed, in milliseconds per character, measured against the voice
 * catalogue rather than against English: Lithuanian averages longer words and
 * the lines lean on case endings that carry meaning at the very end of a
 * sentence, so skimming the first half does not work the way it does in a
 * notification written in English.
 */
const MS_PER_CHAR = 60
/** Below this even „Įrašyta." would blink out before the eye arrives. */
const MIN_MS = 5000
/** Past this a toast is not being read, it is being lived with. */
const MAX_MS = 12_000
/** An undo offer is a safety window, and safety windows do not hurry. */
const UNDO_MIN_MS = 10_000
/** A toast offering reply chips needs long enough to decide to tap one. */
const CHIP_MIN_MS = 9000
/** A trophy is the only time that line will ever be shown. */
const TROPHY_MIN_MS = 9000
/** Screen-reader-only items hold the live region briefly and go. */
const ANNOUNCE_MS = 1500

/** At most this many on screen; the rest wait. Two, because hers are large. */
const VISIBLE_MAX = 2
/**
 * A backstop, not a feature. Nothing in the app can legitimately produce this
 * many pending toasts; if something does, the oldest droppable one goes rather
 * than the queue growing without bound.
 */
const QUEUE_MAX = 8
/** One exit animation. Kept in step with `toast-out` in `app.css`. */
const EXIT_MS = 180

export function durationFor(message: string, kind: ToastKind, hasAction: boolean): number {
  const read = Math.min(MAX_MS, Math.max(MIN_MS, message.length * MS_PER_CHAR))
  if (hasAction) return Math.max(read, UNDO_MIN_MS)
  if (kind === 'trophy') return Math.max(read, TROPHY_MIN_MS)
  return read
}

/**
 * Whether this toast may appear over a performance. An undo offer must: it is
 * the only way back from a deletion that has already committed, and letting it
 * expire unseen behind the Sunday report loses data. An error must, for the
 * same reason in a different shape — a failure nobody was told about is a
 * failure that gets repeated. Everything else can wait until she is done.
 */
function bypassesGate(item: ToastItem): boolean {
  return item.kind === 'error' || item.action != null
}

interface ToastState {
  visible: ToastItem[]
  queue: ToastItem[]
  /** Depth rather than a boolean: performances can, in principle, nest. */
  gates: number
}

let nextId = 1

export const useToasts = create<ToastState>(() => ({ visible: [], queue: [], gates: 0 }))

/**
 * Timers live here rather than in the store because a timer handle is not
 * render state — nothing displays it, and keeping it in the store would make
 * every pause and resume a re-render of every toast on screen.
 */
interface Timer {
  handle: ReturnType<typeof setTimeout>
  startedAt: number
  remainingMs: number
}
const timers = new Map<number, Timer>()

function startTimer(id: number, ms: number): void {
  clearTimer(id)
  timers.set(id, {
    // The bare timer rather than `window.setTimeout`: a toast can be raised
    // from a fire-and-forget chain — the award scan in `sass/context.ts` is
    // one — and that chain can land after the window it started in is gone,
    // which under test is a teardown and in a browser is a closing tab.
    handle: setTimeout(() => expire(id), ms),
    startedAt: Date.now(),
    remainingMs: ms,
  })
}

function clearTimer(id: number): void {
  const timer = timers.get(id)
  if (timer == null) return
  clearTimeout(timer.handle)
  timers.delete(id)
}

function expire(id: number): void {
  timers.delete(id)
  dismiss(id)
}

/** Visible items that are actually occupying a slot. */
function slotsUsed(state: ToastState): number {
  return state.visible.filter((item) => item.srOnly !== true && item.leaving !== true).length
}

/**
 * Move whatever can be shown from the queue onto the screen. Called after
 * every removal and whenever a gate opens; safe to call at any time.
 *
 * Order is preserved absolutely: an item that cannot be shown yet blocks
 * everything behind it rather than being skipped over. A queue that lets the
 * next item jump ahead is not a queue, and the case where it would matter most
 * is the one where it would hurt most — a trophy waiting for the screen while
 * three confirmations of nothing overtake it.
 */
function pump(): void {
  const state = useToasts.getState()
  if (state.queue.length === 0) return

  const promoted: ToastItem[] = []
  const waiting: ToastItem[] = []
  let used = slotsUsed(state)
  // A trophy holds the screen by itself. It arrives word by word with a sound
  // under it, and two of those side by side is not two moments, it is neither
  // of them. Errors and undo offers still come through — see `bypassesGate`.
  let soloBlocks = state.visible.some((item) => item.kind === 'trophy' && item.leaving !== true)

  for (const item of state.queue) {
    const urgent = bypassesGate(item)
    const gated = state.gates > 0 && !urgent
    if (gated || (soloBlocks && !urgent) || used >= VISIBLE_MAX) {
      waiting.push(item)
      continue
    }
    if (item.kind === 'trophy') {
      // Anything behind it waits either way: either because the trophy is now
      // holding the screen, or because it is still waiting for an empty one.
      soloBlocks = true
      if (used > 0) {
        waiting.push(item)
        continue
      }
    }
    promoted.push(item)
    used++
  }
  if (promoted.length === 0) return

  useToasts.setState({ visible: [...state.visible, ...promoted], queue: waiting })
  for (const item of promoted) startTimer(item.id, item.durationMs)
}

function enqueue(item: Omit<ToastItem, 'id'>): number {
  const id = nextId++
  const full: ToastItem = { ...item, id }

  // Screen-reader announcements never queue and never occupy a slot: they are
  // not a thing to look at, they are the live region being given something to
  // say, and delaying one behind a visual queue would read it out long after
  // the action it describes.
  if (full.srOnly === true) {
    useToasts.setState((state) => ({ visible: [...state.visible, full] }))
    startTimer(id, ANNOUNCE_MS)
    return id
  }

  useToasts.setState((state) => {
    const queue = [...state.queue, full]
    if (queue.length <= QUEUE_MAX) return { queue }
    // Over the backstop: shed the oldest thing that is safe to shed. An undo
    // offer and a trophy are never safe to shed — one guards data and the
    // other happens once in the lifetime of the account.
    const index = queue.findIndex((entry) => entry.action == null && entry.kind !== 'trophy')
    return { queue: index === -1 ? queue.slice(0, QUEUE_MAX) : queue.toSpliced(index, 1) }
  })
  pump()
  return id
}

/** Begin this toast's exit. Removal follows one animation later. */
export function dismiss(id: number): void {
  clearTimer(id)
  const item = useToasts.getState().visible.find((entry) => entry.id === id)
  if (item == null) {
    // Still queued: it can simply be forgotten, having never been seen.
    useToasts.setState((state) => ({ queue: state.queue.filter((entry) => entry.id !== id) }))
    return
  }
  if (item.leaving === true) return

  if (item.srOnly === true) {
    useToasts.setState((state) => ({ visible: state.visible.filter((entry) => entry.id !== id) }))
    return
  }

  useToasts.setState((state) => ({
    visible: state.visible.map((entry) => (entry.id === id ? { ...entry, leaving: true } : entry)),
  }))
  // The slot frees the moment the exit starts rather than when it finishes, so
  // a queued toast is already arriving as this one leaves.
  pump()
  setTimeout(() => {
    useToasts.setState((state) => ({ visible: state.visible.filter((entry) => entry.id !== id) }))
  }, EXIT_MS)
}

/** A finger is on this toast. Reading does not happen against a clock. */
export function pressStart(id: number): void {
  const timer = timers.get(id)
  if (timer == null) return
  clearTimeout(timer.handle)
  timers.set(id, {
    handle: timer.handle,
    startedAt: timer.startedAt,
    remainingMs: Math.max(0, timer.remainingMs - (Date.now() - timer.startedAt)),
  })
}

/** The finger left. Resume — unless a tap has since asked it to stay. */
export function pressEnd(id: number): void {
  const item = useToasts.getState().visible.find((entry) => entry.id === id)
  if (item?.held === true) return
  const timer = timers.get(id)
  if (timer == null) return
  startTimer(id, timer.remainingMs)
}

/** Tap to keep it, tap again to let it go. */
export function toggleHold(id: number): void {
  const item = useToasts.getState().visible.find((entry) => entry.id === id)
  if (item == null) return
  const held = item.held !== true
  useToasts.setState((state) => ({
    visible: state.visible.map((entry) => (entry.id === id ? { ...entry, held } : entry)),
  }))
  if (held) {
    pressStart(id)
    return
  }
  const timer = timers.get(id)
  startTimer(id, timer?.remainingMs ?? MIN_MS)
}

/**
 * Close the gate while she performs. The stage calls this on mount and
 * `openGate` on unmount; anything that is not an error or an undo offer waits
 * rather than painting a lime rectangle across the middle of a ceremony.
 */
export function closeGate(): void {
  useToasts.setState((state) => ({ gates: state.gates + 1 }))
}

export function openGate(): void {
  useToasts.setState((state) => ({ gates: Math.max(0, state.gates - 1) }))
  pump()
}

/**
 * One line of feedback, gone by itself. The action is for undo: destructive
 * operations commit immediately and offer the way back here, instead of asking
 * "are you sure" before doing anything.
 *
 * The duration argument is still accepted and still honoured where a caller
 * insists, but every call site in the app has stopped passing one — the length
 * of the sentence is a better answer than any of them were giving.
 */
export function showToast(message: string, action?: ToastAction, durationMs?: number): void {
  enqueue({
    message,
    kind: 'utility',
    action,
    durationMs: durationMs ?? durationFor(message, 'utility', action != null),
  })
}

/** Her, rather than the app. Same behaviour, different face. */
export function showSassToast(
  message: string,
  action?: ToastAction,
  cue?: CueName,
  chips?: ReadonlyArray<ToastAction>,
): void {
  const read = durationFor(message, 'persona', action != null)
  // Chips only where there is no action — the contract on `ToastItem.chips`.
  const offered = action == null && chips != null && chips.length > 0 ? chips : undefined
  enqueue({
    message,
    kind: 'persona',
    action,
    chips: offered,
    cue,
    durationMs: offered != null ? Math.max(read, CHIP_MIN_MS) : read,
  })
}

/**
 * Something failed. Kept visually distinct because the previous version drew a
 * failed reminder save and a logged breakfast identically, and announced both
 * politely.
 */
export function showErrorToast(message: string, action?: ToastAction): void {
  enqueue({
    message,
    kind: 'error',
    action,
    durationMs: durationFor(message, 'error', action != null),
  })
}

/**
 * A toast for something that happens once and will not happen again — an
 * award. Carries her arrival animation, which is why it is a separate door
 * rather than a flag: the effect only means anything as long as ordinary
 * feedback never uses it.
 */
export function showTrophyToast(message: string, cue?: CueName): void {
  enqueue({ message, kind: 'trophy', cue, durationMs: durationFor(message, 'trophy', false) })
}

/** Announce to screen readers only, through the same live region. */
export function announce(message: string): void {
  enqueue({ message, kind: 'utility', srOnly: true, durationMs: ANNOUNCE_MS })
}

/**
 * Test-only: everything raised, on screen or waiting, oldest first.
 *
 * The store deliberately keeps the two apart — that separation is the feature —
 * but a test asserting "she said this" does not care which side of the queue
 * the sentence is sitting on.
 */
export function __toastsForTests(): ToastItem[] {
  const { visible, queue } = useToasts.getState()
  return [...visible, ...queue]
}

/** Test-only: drop everything and forget every timer. */
export function __resetToastsForTests(): void {
  for (const id of timers.keys()) clearTimer(id)
  timers.clear()
  useToasts.setState({ visible: [], queue: [], gates: 0 })
}
